import React, { useRef, useState, useMemo, useCallback } from 'react';
import _ from 'lodash';
import { basedAxios as Axios, useStore } from '../../store/Store';
import PlaceholderImage from '../../assets/profile_image_placeholder.svg';
import { ProfileImageCroppingTool } from './components/ProfileImageCroppingTool';
import { runInAction, toJS } from 'mobx';
import EventLabel from '../../components/EventLabel/EventLabel';
import cx from 'classnames';
import Asterisk from '../../components/Asterisk/Asterisk';

export const ProfileImageUploader = ({ settings, validations, contentModuleId, validate }) => {
  const [sourceData, setSourceData] = useState(null);
  const [fileType, setFileType] = useState('image/png');
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [isCropping, setIsCropping] = useState(false);
  const [fileSizeWarning, setFileSizeWarning] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  const fuRef = useRef();

  const { activePersonNo, orders, environment, updateOrder, getFilteredValidations } = useStore();
  const { shape, viewport, hidden, required } = settings;

  const validity = useMemo(() => validations.find(v => v.id === contentModuleId), [validations]);

  const sourceImage = useMemo(() => {
    if (orders?.[activePersonNo]?.profileImage) {
      if (required) {
        if (orders?.[activePersonNo]?.profileImage.source || orders?.[activePersonNo]?.profileImage.file) {
          runInAction(() => {
            validity.valid = true;
          });
          console.log('Valid', required, orders?.[activePersonNo]?.profileImage.source)
        }
        else {
          runInAction(() => {
            validity.valid = false;
          });
          console.log('Not Valid', required, orders?.[activePersonNo]?.profileImage.source)
        }
      }
      
      return orders[activePersonNo].profileImage.source + `?rnd=${+new Date()}`;
    }
    if (settings.defaultImage) return settings.defaultImage;
    return PlaceholderImage;
  }, [settings, orders, activePersonNo]);

  const toBase64 = file =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });

  const onImageUpload = async e => {
    const file = e.target.files[0];
    const result = await toBase64(file).catch(e => Error(e));
    if (result instanceof Error) {
      console.error('Error: ', result.message);
      return;
    } else {
      setSourceData(result);
      setFileType(file.type);
      setFileSizeWarning(false);
     
    }
  };
  
  const startCroppingCurrent = async () => {
    const result = await fetch(sourceImage);
    const blobObj = await result.blob();
    const b64s = await toBase64(blobObj);
    setSourceData(b64s);
    setFileType(blobObj.type);
    setFileSizeWarning(false);
  };

  const setImageOnOrder = useCallback(
    async croppedData => {
      const maxFileSize = 30000000;
      const newOrder = _.cloneDeep(toJS(orders[activePersonNo]));

      if (!newOrder.profileImage) {
        newOrder.profileImage = { source: '' };
      }

      fuRef.current.value = null;

      if (croppedData.length >= maxFileSize) {
        setFileSizeWarning(true);
        return;
      }

      setUploadInProgress(true);

      try {
        const { data } = await Axios.post(`./files/profileimagetemp/${environment.eventId}`, {
          file: croppedData,
          name: `${newOrder.sqlStateNo + '_' + activePersonNo}_ProfileImage.png`,
          type: fileType,
        });
        
        newOrder.profileImage.source = data;
        newOrder.profileImage.file = data;
  
        updateOrder(newOrder);
        setSourceData(null);
        setUploadInProgress(false);

        
      } catch { }
    },
    [orders, activePersonNo]
  );

  const onCropped = croppedData => {
    setImageLoaded(false);
    setImageOnOrder(croppedData);
  };

  const removeImage = useCallback(() => {
    const newOrder = _.cloneDeep(toJS(orders[activePersonNo]));

    if (newOrder.profileImage) {
      newOrder.profileImage = null;
      updateOrder(newOrder);
      
      if (required) {
        runInAction(() => {
          validity.valid = false;
        });
      }
    }
  }, [orders, activePersonNo]);

  const handleImageLoaded = () => setImageLoaded(true);
  
  if (hidden) return null;

  return (
    <div className="profile-upload-wrap">
      <div className={`image-container justify-center`}>
        <div
          className="relative flex justify-end items-end flex-col overflow-hidden"
          style={{
            width: viewport.width + 'px',
            height: viewport.height + 'px',
            borderRadius: shape == 'circle' ? '50%' : 0,
            background: sourceImage ? 'transparent' : undefined,
          }}
        >
          {uploadInProgress && (
            <div className="absolute inset-0 bg-transparent z-40">
              <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
          )}
          {sourceData || isCropping ? (
            <ProfileImageCroppingTool
              src={sourceData}
              fileType={fileType}
              shape={shape}
              onCropped={onCropped}
              onCancel={() => setSourceData(null)}
              height={viewport.height}
              width={viewport.width}
            />
          ) : (
            <img onLoad={handleImageLoaded} src={sourceImage} className={cx('w-full')} alt="image" />
          )}
        </div>
      </div>

      <div className="profile-upload-btn-wrap relative gap-2 flex-wrap">
        <button className={cx('cms-button profile-upload-button', (!validity.valid && validate === true) ? '!text-red-700' : null)} disabled={sourceData} onClick={e => fuRef.current.click()}>
          <i className="ri-folder-line mr-2" />
          <EventLabel name="UploadProfileImg" groupName="Cms.FileUpload" />
          {required ? <Asterisk /> : null}
        </button>

        {(sourceData || isCropping) && fileSizeWarning ? (
          <span className="absolute -top-8 left-8 z-10 bg-red-100 text-red-700 text-xs rounded-md p-1 px-2 font-semibold">
            <i className="ri-alert-line mr-1" />
            <EventLabel name="FileSizeTooBig" groupName="Cms.FileUpload" />
          </span>
        ) : null}
        {orders?.[activePersonNo]?.profileImage ? (
          <button className="cms-button" disabled={sourceData} onClick={() => startCroppingCurrent()}>
            <i className="ri-crop-line mr-1" />
            <EventLabel name="CropProfileImg" groupName="Cms.FileUpload" />
          </button>
        ) : null}
        {orders?.[activePersonNo]?.profileImage?.source && (
          <button className="cms-button profile-upload-button bg-[rgba(216,90,90)_!important] text-white" onClick={removeImage}>
            <i className="ri-delete-bin-3-line mr-2" />
            <EventLabel name="RemoveImage" groupName="Cms.FileUpload" />
          </button>
        )}
      </div>
      <input ref={fuRef} id="file-upload-pfi" className="profile-file-upload" type="file" accept="image/*" onChange={e => onImageUpload(e)} />
    </div>
  );
};
